import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { UserRole, Package, PackageStatus, CourierStatus, DeliveryEvidence, Pharmacy, AuthSession, AuthUser, ChatConversation, Address, StatusEvent, Institution, PharmacyPackageCount } from './types';
import Layout from './components/Layout';
import LoginScreen from './components/LoginScreen';
import PharmacyView from './components/PharmacyView';
import AdminView from './components/AdminView';
import CourierView from './components/CourierView';
import CourierPharmacyLink from './components/CourierPharmacyLink';
import DienstCheck from './components/DienstCheck';
import PhoneNumberGate from './components/PhoneNumberGate';
import CourierRooster from './components/CourierRooster';
import RegionManagerView from './components/RegionManagerView';
import PlannerRedirectView from './components/PlannerRedirectView';
import SupervisorView from './components/SupervisorView';
import SuperuserView from './components/SuperuserView';
import PatientView from './components/PatientView';
import InstitutionSelector from './components/InstitutionSelector';
import Scanner from './Scanner';
import ManualAddressForm from './components/ManualAddressForm';
import ChatBot from './components/ChatBot';
import { optimizeRoute, optimizeRouteDetailed, type RouteGeometry } from './services/geminiService';
import RouteMapModal from './components/RouteMapModal';
import { getSession, logout, saveSession, getCourierPharmacies } from './services/authService';
import { db, supabase, getAuthHeaders } from './services/supabaseService';
import { getMyPhoneStatus } from './services/courierContactService';
import { filterPharmacies, filterPackagesByAccess } from './utils/pharmacyAccess';
import { addressKey } from './utils/addressKey';
import { Cloud, CloudOff, RefreshCw, AlertTriangle, ChevronDown, ChevronUp, Copy, Check, Info, X, Building2, Trash2, Plus, Loader2, Truck, Calendar, CalendarDays, ArrowUpRight } from 'lucide-react';

const COURIER_NAMES: Record<string, string> = {
  'k1': 'Marco Koerier',
  'k2': 'Sanne Bezorgd',
};

// Normaliseer NL-postcode: "1217nl" / "1217 nl" / "1217NL" → "1217 NL".
// Geeft de invoer ongewijzigd terug als hij niet matcht (bv. buitenlands of incompleet).
const normalizePostcode = (pc: string): string =>
  pc.replace(/^(\d{4})\s*([A-Z]{2})$/i, '$1 $2').toUpperCase();

// Statussen die meetellen als "ligt nog in de rit" voor de dubbel-adresmelding
const OPEN_STATUSES = [
  PackageStatus.SCANNING,
  PackageStatus.PENDING,
  PackageStatus.ASSIGNED,
  PackageStatus.PICKED_UP,
];

// Enige definitie van "nog te bezorgen". De pakket-variant hieronder en de
// deliveredAt-regel in updateMultipleStatus leiden hier allebei van af.
const isActionableStatus = (status: PackageStatus): boolean =>
  [PackageStatus.ASSIGNED, PackageStatus.PICKED_UP].includes(status);

const enrichWithHistory = (pkg: Package): Package => {
  if (pkg.statusHistory && pkg.statusHistory.length > 0) return pkg;
  const history: StatusEvent[] = [{ status: PackageStatus.PENDING, timestamp: pkg.createdAt }];
  if (pkg.deliveredAt && pkg.status !== PackageStatus.PENDING) {
    history.push({
      status:    pkg.status,
      timestamp: pkg.deliveredAt,
      note:      pkg.deliveryEvidence?.deliveryNote,
    });
  }
  return { ...pkg, statusHistory: history };
};

// ── Apotheek bewerken modal ────────────────────────────────────────────
const EditPharmacyModal: React.FC<{
  pharmacy:  Pharmacy;
  onSave:    (updated: Pharmacy) => Promise<void>;
  onClose:   () => void;
  onDelete?: (pharmacy: Pharmacy) => Promise<void>;
}> = ({ pharmacy, onSave, onClose, onDelete }) => {
  const [name,    setName]    = useState(pharmacy.name);
  const [street,      setStreet]      = useState(pharmacy.street ?? '');
  const [houseNumber, setHouseNumber] = useState(pharmacy.houseNumber ?? '');
  const [postalCode,  setPostalCode]  = useState(pharmacy.postalCode ?? '');
  const [city,        setCity]        = useState(pharmacy.city ?? '');
  const [code,    setCode]    = useState(pharmacy.code ?? '');
  const [hourlyRate, setHourlyRate] = useState(pharmacy.hourlyRate?.toString() ?? '');
  const [saving,  setSaving]  = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Groep: superuser kiest uit de lijst, supervisor zit vast op de eigen groep
  const sess = getSession();
  const myRole = sess?.user.role;
  const myGroupId = sess?.user.groupId;
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [groupId, setGroupId] = useState(pharmacy.groupId ?? '');
  useEffect(() => { db.fetchGroups().then(setGroups).catch(() => {}); }, []);
  useEffect(() => {
    if (myRole === UserRole.SUPERVISOR && myGroupId) setGroupId(myGroupId);
  }, [myRole, myGroupId]);

  // Nieuwe groep aanmaken vanuit dit scherm (alleen superuser)
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [newGroupName,  setNewGroupName]  = useState('');
  const [groupBusy,     setGroupBusy]     = useState(false);
  const [groupError,    setGroupError]    = useState('');

  const handleCreateGroup = async () => {
    const naam = newGroupName.trim();
    if (!naam) return;
    setGroupBusy(true); setGroupError('');
    try {
      const created = await db.createGroup(naam);
      setGroups(await db.fetchGroups());
      setGroupId(created.id);
      setCreatingGroup(false);
      setNewGroupName('');
    } catch (e: any) {
      setGroupError(e?.message || 'Aanmaken mislukt');
    } finally {
      setGroupBusy(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({
        ...pharmacy,
        name:        name.trim(),
        street:      street.trim() || undefined,
        houseNumber: houseNumber.trim() || undefined,
        postalCode:  postalCode.trim() || undefined,
        city:        city.trim() || undefined,
        groupId:     (myRole === UserRole.SUPERVISOR ? (myGroupId ?? '') : groupId) || undefined,
        code:        code.trim() || undefined,
        hourlyRate:  hourlyRate.trim() ? parseFloat(hourlyRate) : 0,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete(pharmacy);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
      style={{ background: 'rgba(25,28,30,0.60)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
    >
      <div
        className="bg-white rounded-4xl w-full max-w-md max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom-4 duration-300"
        style={{ boxShadow: '0 24px 64px rgba(25,28,30,0.20)' }}
      >
        <div className="flex items-center justify-between px-7 pt-7 pb-5">
          <div>
            <h2 className="text-xl font-display font-black text-[#191c1e]">Apotheek bewerken</h2>
            <p className="text-[10px] font-display font-black text-[#3d4945]/60 uppercase tracking-widest mt-0.5">{pharmacy.name}</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-[#f2f4f6] flex items-center justify-center text-[#3d4945] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-7 py-6 space-y-4">
          {[
            { label: 'Naam apotheek', placeholder: 'bijv. Apotheek de Kroon', val: name,        set: setName,        required: true  },
            { label: 'Straat',        placeholder: 'bijv. Hoofdstraat',       val: street,      set: setStreet,      required: false },
            { label: 'Huisnummer',    placeholder: 'bijv. 12A',               val: houseNumber, set: setHouseNumber, required: false },
            { label: 'Postcode',      placeholder: 'bijv. 1234 AB',           val: postalCode,  set: setPostalCode,  required: false },
            { label: 'Plaats',        placeholder: 'bijv. Hilversum',         val: city,        set: setCity,        required: false },
            { label: 'Interne code',  placeholder: 'bijv. KRO',               val: code,        set: setCode,        required: false },
          ].map(f => (
            <div key={f.label} className="space-y-1.5">
              <label className="text-[10px] font-display font-black uppercase tracking-widest text-[#3d4945]/60 ml-1">
                {f.label} {f.required && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={f.val}
                onChange={e => f.set(e.target.value)}
                placeholder={f.placeholder}
                required={f.required}
                className="w-full bg-white rounded-xl px-5 h-12 font-body font-bold text-[#191c1e] text-sm outline-none transition-all"
                style={{ boxShadow: '0 0 0 1px rgba(188,202,196,0.2)' }}
                onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px #006b5a40'}
                onBlur={e => e.currentTarget.style.boxShadow = '0 0 0 1px rgba(188,202,196,0.2)'}
              />
            </div>
          ))}

          {pharmacy.address && !pharmacy.street && (
            <p className="text-[11px] text-[#3d4945]/60 -mt-2 ml-1">Oud adres: {pharmacy.address}</p>
          )}

          {/* Groep / regio */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-display font-black uppercase tracking-widest text-[#3d4945]/60 ml-1">
              Groep / regio
            </label>
            {myRole === UserRole.SUPERVISOR ? (
              <div className="w-full bg-[#f2f4f6] rounded-xl px-5 h-12 flex items-center font-body font-bold text-[#191c1e] text-sm">
                {groups.find(g => g.id === myGroupId)?.name ?? myGroupId ?? 'Geen groep toegewezen'}
              </div>
            ) : (
              <>
                <select
                  value={groupId}
                  onChange={e => setGroupId(e.target.value)}
                  className="w-full bg-white rounded-xl px-5 h-12 font-body font-bold text-[#191c1e] text-sm outline-none"
                  style={{ boxShadow: '0 0 0 1px rgba(188,202,196,0.2)' }}
                >
                  <option value="">— Geen groep —</option>
                  {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>

                {!creatingGroup ? (
                  <button
                    type="button"
                    onClick={() => { setCreatingGroup(true); setGroupError(''); }}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-display font-black uppercase tracking-widest text-[#006b5a]"
                  >
                    <Plus size={13} /> Nieuwe groep
                  </button>
                ) : (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newGroupName}
                        onChange={e => setNewGroupName(e.target.value)}
                        placeholder="Naam nieuwe groep"
                        className="flex-1 bg-white rounded-xl px-4 h-11 font-body font-bold text-[#191c1e] text-sm outline-none"
                        style={{ boxShadow: '0 0 0 1px rgba(188,202,196,0.2)' }}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreateGroup(); } }}
                      />
                      <button
                        type="button"
                        onClick={handleCreateGroup}
                        disabled={groupBusy || !newGroupName.trim()}
                        className="h-11 px-4 rounded-xl bg-[#006b5a] text-white font-display font-black text-sm flex items-center gap-1 disabled:opacity-50"
                      >
                        {groupBusy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                        Aanmaken
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => { setCreatingGroup(false); setNewGroupName(''); setGroupError(''); }}
                        className="text-[11px] font-display font-black uppercase tracking-widest text-[#3d4945]/50"
                      >
                        Annuleren
                      </button>
                      {groupError && <span className="text-[11px] text-red-500">{groupError}</span>}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Uurtarief — wat GoBob deze apotheek factureert */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-display font-black uppercase tracking-widest text-[#3d4945]/60 ml-1">
              Uurtarief (€/uur)
            </label>
            <input
              type="number"
              step="0.50"
              min="0"
              value={hourlyRate}
              onChange={e => setHourlyRate(e.target.value)}
              placeholder="45.00"
              className="w-full bg-white rounded-xl px-5 h-12 font-body font-bold text-[#191c1e] text-sm outline-none transition-all"
              style={{ boxShadow: '0 0 0 1px rgba(188,202,196,0.2)' }}
              onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px #006b5a40'}
              onBlur={e => e.currentTarget.style.boxShadow = '0 0 0 1px rgba(188,202,196,0.2)'}
            />
          </div>

          <div className="flex gap-3 pt-2 items-center">
            {onDelete && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting || saving}
                className="flex items-center gap-2 px-4 h-12 rounded-full text-red-500 bg-red-50 border border-red-100 font-display font-bold text-sm hover:bg-red-100 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Apotheek verwijderen"
              >
                <Trash2 size={16} />
                {deleting ? 'Bezig…' : 'Verwijderen'}
              </button>
            )}
            <div className="flex-1" />
            <button
              type="button"
              onClick={onClose}
              className="h-12 px-5 rounded-full font-display font-semibold text-sm text-[#101c30] bg-[#d7e2fe] transition-all active:scale-95"
            >
              Annuleren
            </button>
            <button
              type="submit"
              disabled={saving || deleting || !name.trim()}
              className="h-12 px-5 rounded-full text-white font-display font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #006b5a, #48c2a9)' }}
            >
              {saving ? <span className="animate-spin inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full" /> : null}
              {saving ? 'Opslaan...' : 'Opslaan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [showPatientView, setShowPatientView] = useState(false);
  const [packages, setPackages] = useState<Package[]>([]);
  const [pharmacies, setPharmacies] = useState<Pharmacy[]>([]);
  const [showScanner, setShowScanner] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [routeGeometry, setRouteGeometry] = useState<RouteGeometry | null>(null);
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [toast, setToast]                 = useState<string | null>(null);
  const [cloudStale, setCloudStale]       = useState(false);
  // Letterlijke melding van een mislukte pakket-lees, en of die door een
  // verlopen sessie kwam. Zonder dat onderscheid zag "0 pakketten" eruit als
  // een echte stand terwijl de query als anon de deur uit ging.
  const [packagesError, setPackagesError]   = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Pakket-tellingen per apotheek uit de database (migratie 019). null = nog
  // niet of niet beschikbaar; het overzicht telt dan zelf uit `packages`.
  const [packageCounts, setPackageCounts] = useState<PharmacyPackageCount[] | null>(null);
  const [isSyncing, setIsSyncing]         = useState(false);
  const [showSetupHelp, setShowSetupHelp] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pharmacyMismatch, setPharmacyMismatch] = useState<string | null>(null);
  const [unknownPharmacy, setUnknownPharmacy] = useState<string | null>(null);
  // Superuser-specific: can pick which pharmacy to act as
  const [superuserPharmacyId, setSuperuserPharmacyId] = useState<string>('');

  // Courier: welke apotheken zitten in de huidige rit
  const [courierPharmacyIds, setCourierPharmacyIds] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('courierPharmacyIds');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (courierPharmacyIds.length > 0) {
      localStorage.setItem('courierPharmacyIds', JSON.stringify(courierPharmacyIds));
    } else {
      localStorage.removeItem('courierPharmacyIds');
    }
  }, [courierPharmacyIds]);

  // Courier: de door de koerier gekozen "actieve apotheek". Bepaalt aan welke
  // apotheek nieuw gescande pakketten worden gekoppeld (OCR is enkel controle).
  const [activeScanPharmacyId, setActiveScanPharmacyId] = useState<string | null>(null);

  // Ref spiegelt de actieve apotheek zodat handleNewScan (refs i.p.v. closures)
  // de actuele waarde leest zonder dat de useCallback-deps wijzigen.
  const activeScanPharmacyIdRef = useRef<string | null>(activeScanPharmacyId);
  useEffect(() => { activeScanPharmacyIdRef.current = activeScanPharmacyId; }, [activeScanPharmacyId]);

  // Koerier: toont het koppel-/kiesscherm (CourierPharmacyLink) om de actieve
  // apotheek te wisselen, ook als er al een gekozen is.
  const [showPharmacyPicker, setShowPharmacyPicker] = useState(false);



  // Vaste instellingen
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [showInstitutionSelector, setShowInstitutionSelector] = useState(false);
  const [activeInstitutionRoute, setActiveInstitutionRoute] = useState<Institution[]>([]);

  const hasCloudConfig = !!supabase;
  const role = session?.user.role ?? null;
  // Planner-toegang staat los van de rol (migratie 016). `=== true` omdat een
  // sessie die vóór die migratie in localStorage is gezet het veld niet heeft.
  const hasPlannerAccess = session?.user.isPlanner === true;
  // De lokale sessie is nog niet geverifieerd tegen Supabase. Zolang dat loopt
  // tonen we het loginscherm niet: anders flitst dat bij elke page-load even
  // voorbij voor iedereen die wél gewoon is ingelogd.
  const [sessionChecking, setSessionChecking] = useState(() => {
    try { return !!localStorage.getItem('gobob_session'); } catch { return false; }
  });
  const [dienstCheckOk, setDienstCheckOk] = useState(() => {
    try { return sessionStorage.getItem('gs_dienstcheck_ok') === '1'; } catch { return false; }
  });

  // Koerier: actief tabblad — bezorgen (de rit) of het eigen rooster.
  const [courierTab, setCourierTab] = useState<'bezorgen' | 'rooster'>('bezorgen');

  // Koerier: staat er al een telefoonnummer in courier_contacts? null betekent
  // "nog niet gecontroleerd", en dat is expliciet iets anders dan "nee".
  const [courierPhoneOk, setCourierPhoneOk] = useState<boolean | null>(null);

  // Restore session on mount.
  //
  // De lokale sessie (gobob_session in localStorage) en de Supabase-auth-sessie
  // zijn twee verschillende dingen. Een verlopen of ingetrokken refresh-token
  // laat gobob_session ongemoeid: de app rendert dan een volledig dashboard
  // terwijl elke RLS-query als anon de deur uit gaat en leeg terugkomt — dat was
  // de "0 pakketten bij alle apotheken"-storing. Een lokale sessie geldt daarom
  // alleen als geldig zolang Supabase er een token bij heeft; zo niet, dan
  // ruimen we hem op en komt het loginscherm.
  useEffect(() => {
    const existing = getSession();
    if (!existing) { setSessionChecking(false); return; }
    if (!supabase) { setSession(existing); setSessionChecking(false); return; }

    let cancelled = false;
    (async () => {
      // getSession() ververst een verlopen token zelf en geeft null terug als
      // het refresh-token niet meer geldig is.
      const { data: { session: sbSession } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (sbSession?.access_token) {
        setSession(existing);
        setSessionChecking(false);
        return;
      }
      console.warn('[Auth] Lokale sessie zonder Supabase-token — opruimen en opnieuw inloggen');
      await logout();
      if (cancelled) return;
      setSession(null);
      setPackages([]);
      setSessionChecking(false);
    })();
    return () => { cancelled = true; };
  }, []);

  // Load data once session exists (or for patient view)
  useEffect(() => {
    if (!session && !showPatientView) return;
    const loadData = async () => {
      setIsSyncing(true);
      const [pkgs, pharms] = await Promise.all([db.fetchPackages(), db.fetchPharmacies()]);

      // Wijs scanNumber retroactief toe op basis van aanmaakdatum (per apotheek)
      const enriched = pkgs
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .map((pkg, index) => enrichWithHistory({
          ...pkg,
          scanNumber: pkg.scanNumber ?? index + 1,
        }));
      setPackages(enriched);

      setPharmacies(pharms);
      if (pharms.length > 0 && !superuserPharmacyId) {
        setSuperuserPharmacyId(pharms[0].id);
      }
      // fetchPharmacies kan _cloudReadFailed weer op false zetten (ze draaien
      // parallel), dus de pakket-fout telt hier ook los mee.
      const pkgError = db.lastReadError();
      setPackagesError(pkgError);
      setSessionExpired(db.sessionExpired());
      setCloudStale(db.cloudReadFailed() || pkgError !== null);
      setIsSyncing(false);
    };
    loadData();
  }, [session, showPatientView]);

  // Derive the "current pharmacy" based on role
  const currentPharmacy: Pharmacy = useMemo(() => {
    if (!session) return { id: 'ph-1', name: 'Apotheek de Kroon' };
    if (role === UserRole.SUPERUSER) {
      return pharmacies.find(p => p.id === superuserPharmacyId) || pharmacies[0] || { id: 'ph-1', name: 'Apotheek de Kroon' };
    }
    if (session.user.pharmacyId) {
      return pharmacies.find(p => p.id === session.user.pharmacyId) || { id: session.user.pharmacyId, name: 'Mijn Apotheek' };
    }
    return pharmacies[0] || { id: 'ph-1', name: 'Apotheek de Kroon' };
  }, [session, role, pharmacies, superuserPharmacyId]);

  // Laad vaste instellingen — koerier ziet alle instellingen, andere rollen gefilterd op apotheek
  useEffect(() => {
    if (!session) return;
    if (role === UserRole.COURIER) {
      db.fetchInstitutions()
        .then(setInstitutions)
        .catch(() => setInstitutions([]));
      return;
    }
    const pid = currentPharmacy.id;
    if (!pid) { setInstitutions([]); return; }
    db.fetchInstitutions(pid)
      .then(setInstitutions)
      .catch(() => setInstitutions([]));
  }, [session, role, currentPharmacy.id]);

  // Load conversations + realtime subscription voor pharmacy staff
  useEffect(() => {
    if (!session) return;
    const r = session.user.role;
    if (r !== UserRole.PHARMACY && r !== UserRole.ADMIN && r !== UserRole.SUPERUSER) return;

    // Eerste load (localStorage + Supabase)
    db.fetchConversations(currentPharmacy.id).then(setConversations).catch(() => {});

    // Realtime subscription (alleen als Supabase beschikbaar is)
    if (!supabase) return;
    const channel = supabase
      .channel('chat_conversations_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_conversations',
          filter: `pharmacyId=eq.${currentPharmacy.id}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setConversations(prev => [payload.new as ChatConversation, ...prev]);
            setToast('Nieuw patiëntgesprek ontvangen');
            setTimeout(() => setToast(null), 4000);
          }
          if (payload.eventType === 'UPDATE') {
            setConversations(prev =>
              prev.map(c =>
                c.id === (payload.new as ChatConversation).id
                  ? (payload.new as ChatConversation)
                  : c
              )
            );
          }
          if (payload.eventType === 'DELETE') {
            setConversations(prev =>
              prev.filter(c => c.id !== (payload.old as { id: string }).id)
            );
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [session, currentPharmacy.id]);

  // Realtime subscription voor pharmacies (zichtbaar voor alle authenticated gebruikers)
  useEffect(() => {
    if (!supabase) return;
    const channel = supabase
      .channel('pharmacies_changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'pharmacies' },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setPharmacies(prev =>
              prev.some(p => p.id === (payload.new as Pharmacy).id)
                ? prev
                : [...prev, payload.new as Pharmacy]
            );
          }
          if (payload.eventType === 'UPDATE') {
            setPharmacies(prev =>
              prev.map(p => p.id === (payload.new as Pharmacy).id ? payload.new as Pharmacy : p)
            );
          }
          if (payload.eventType === 'DELETE') {
            setPharmacies(prev =>
              prev.filter(p => p.id !== (payload.old as Pharmacy).id)
            );
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  const handleMarkConversationRead = (id: string) => {
    setConversations(prev => prev.map(c => c.id === id ? { ...c, isRead: true } : c));
    db.markConversationRead(id).catch(() => {});
  };

  const handleMarkCallbackHandled = (id: string) => {
    setConversations(prev => prev.map(c => {
      if (c.id !== id || !c.callbackRequest) return c;
      const updated = { ...c, callbackRequest: { ...c.callbackRequest, isHandled: true } };
      db.saveConversation(updated).catch(() => {});
      return updated;
    }));
  };

  // Package filter per role
  const visiblePackages = useMemo(() => {
    if (!session) return packages;
    switch (role) {
      case UserRole.SUPERUSER:
      case UserRole.SUPERVISOR:
        return packages;
      case UserRole.ADMIN:
      case UserRole.PHARMACY:
        return session.user.pharmacyId
          ? packages.filter(p => p.pharmacyId === session.user.pharmacyId)
          : packages;
      case UserRole.COURIER: {
        const today = new Date().toDateString();
        return session.user.courierId
          ? packages.filter(p =>
              p.courierId === session.user.courierId &&
              new Date(p.createdAt).toDateString() === today
            )
          : packages.filter(p =>
              new Date(p.createdAt).toDateString() === today
            );
      }
      default:
        return packages;
    }
  }, [packages, session, role, courierPharmacyIds]);

  // Apotheken en pakketten gefilterd op wat de ingelogde gebruiker mag zien
  const accessiblePharmacies = useMemo(
    () => (session ? filterPharmacies(session.user, pharmacies) : []),
    [session, pharmacies],
  );

  const accessiblePackages = useMemo(
    () => (session ? filterPackagesByAccess(session.user, packages, pharmacies) : []),
    [session, packages, pharmacies],
  );

  // Pakket-tellingen per apotheek server-side ophalen, voor de rollen die het
  // apotheek-overzicht zien. Dit vervangt het tellen in de browser: dat ging
  // over `packages`, en die lijst kan door de PostgREST-grens van 1000 rijen
  // onvolledig zijn.
  //
  // De superuser vraagt null (= alles wat RLS toelaat, inclusief pakketten
  // zonder echte apotheek). De andere rollen geven hun eigen apotheken mee,
  // zodat de tellers bij hun selectie horen.
  useEffect(() => {
    if (!session) { setPackageCounts(null); return; }
    const r = session.user.role;
    if (r !== UserRole.SUPERUSER && r !== UserRole.SUPERVISOR && r !== UserRole.ADMIN) {
      setPackageCounts(null);
      return;
    }

    const scope = r === UserRole.SUPERUSER ? null : accessiblePharmacies.map(p => p.id);
    // Een lege lijst zou nul rijen opleveren en er precies uitzien als de
    // storing die we aan het verhelpen zijn. Dan liever niets ophalen en de
    // browser-telling laten staan.
    if (scope !== null && scope.length === 0) { setPackageCounts(null); return; }

    let cancelled = false;
    db.fetchPharmacyPackageCounts(scope)
      .then(rows => { if (!cancelled) setPackageCounts(rows); })
      .catch(() => { if (!cancelled) setPackageCounts(null); });
    return () => { cancelled = true; };
  }, [session, packages, accessiblePharmacies]);

  const handleLogin = (user: AuthUser, _activePharmacyId?: string) => {
    const sess = { user, loggedInAt: new Date().toISOString() };
    setSession(sess);
    // Apotheek-keuze gebeurt niet meer bij login; de useEffect hieronder
    // laadt automatisch alle gekoppelde apotheken voor een courier.
  };

  // Bij iedere COURIER-login: vraag Supabase wat de gekoppelde apotheken
  // van deze koerier zijn en overschrijf localStorage/state daarmee. Voorkomt
  // dat een stale cache (oude apotheken van eerdere ritten) blijft hangen.
  // Bij lege Supabase-respons valt de bestaande localStorage-waarde terug.
  useEffect(() => {
    if (!session || session.user.role !== UserRole.COURIER) return;
    let cancelled = false;
    (async () => {
      const ids = await getCourierPharmacies().catch(() => [] as string[]);
      const allIds = Array.from(new Set([
        ...ids,
        ...(session.user.pharmacyIds ?? []),
        ...(session.user.pharmacyId ? [session.user.pharmacyId] : []),
      ]));
      if (cancelled) return;
      if (allIds.length > 0) {
        // Supabase heeft data → altijd overschrijven (negeer cache)
        setCourierPharmacyIds(allIds);
        localStorage.setItem('courierPharmacyIds', JSON.stringify(allIds));
      }
      // Geen data uit Supabase → laat bestaande localStorage-state intact
    })();
    return () => { cancelled = true; };
  }, [session]);

  // Bij iedere COURIER-login: controleer of hij een nummer heeft in
  // courier_contacts. Zonder rij valt hij zonder logregel uit sms_due_shifts()
  // van de planner, dus vragen we het alsnog voordat hij de app in mag.
  useEffect(() => {
    if (!session || session.user.role !== UserRole.COURIER) { setCourierPhoneOk(null); return; }
    let cancelled = false;
    (async () => {
      const status = await getMyPhoneStatus();
      if (cancelled) return;
      // Bij een onbekende uitkomst (demo-account zonder JWT, netwerkhik) laten we
      // hem door. Een koerier die door een storing niet aan zijn rit kan beginnen
      // is een groter probleem dan een dag langer zonder nummer in de tabel.
      setCourierPhoneOk(status.known ? status.hasPhone : true);
    })();
    return () => { cancelled = true; };
  }, [session]);

  const handleLogout = async () => {
    if (confirm('Uitloggen?')) {
      await logout();
      setSession(null);
      setPackages([]);
      setCourierPharmacyIds([]);
      localStorage.removeItem('courierPharmacyIds');
    }
  };

  // Start elke scansessie op 1 — het scannummer is enkel een hulpmiddel voor de
  // koerier om op het pakje te schrijven, niet uniek over koeriers of dagen heen.
  const nextScanNumberRef  = useRef<number>(1);
  async function geocodeAddress(address: Address): Promise<{ lat: number; lng: number } | null> {
    console.log('[Geocode] Aanroep gestart voor:', address.street, address.houseNumber);
    const normalizedAddress: Address = {
      ...address,
      postalCode: normalizePostcode(address.postalCode ?? ''),
    };
    try {
      const response = await fetch('/.netlify/functions/maps', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify({
          action: 'geocode',
          addresses: [
            `${normalizedAddress.street} ${normalizedAddress.houseNumber}, ` +
            `${normalizedAddress.postalCode} ${normalizedAddress.city}, Netherlands`,
          ],
        }),
      });

      if (!response.ok) {
        const errText = await response.text();
        console.error('[Geocode] Server fout:', response.status, errText);
        return null;
      }

      const data = await response.json();

      if (!data.results) {
        console.error('[Geocode] Geen results in response:', data);
        return null;
      }

      let coords = data.results[0] ?? null;

      if (!coords) {
        // Fallback: probeer geocoding op enkel postcode + huisnummer (zonder straat/stad).
        // Lost adressen op waar de straatnaam van het label net iets afwijkt van Google.
        console.warn('[Geocode] Geen coords voor volledig adres, probeer postcode-fallback:', address.street, address.houseNumber);
        try {
          const fallbackResponse = await fetch('/.netlify/functions/maps', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
            body: JSON.stringify({
              action: 'geocode',
              addresses: [
                `${normalizedAddress.postalCode} ${address.houseNumber}, Netherlands`,
              ],
            }),
          });
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            coords = fallbackData.results?.[0] ?? null;
            if (coords) {
              console.log('[Geocode] Fallback postcode werkte voor:', address.postalCode, address.houseNumber);
            }
          }
        } catch (fbErr) {
          console.error('[Geocode] Fallback netwerk fout:', fbErr);
        }
      }

      if (!coords) {
        console.warn('[Geocode] Geen coords (ook niet via fallback) voor:', address.street, address.houseNumber);
        return null;
      }

      console.log('[Geocode] ✓', address.street, address.houseNumber, '→', coords.lat, coords.lng);
      return coords;
    } catch (err) {
      console.error('[Geocode] Netwerk fout:', err);
      return null;
    }
  }

  // Ref zodat handleNewScan altijd de actuele packages ziet zonder afhankelijk te zijn
  // van de packages-state in de closure (voorkomt stale-closure race condition bij burst scans)
  const packagesRef      = useRef<Package[]>(packages);
  useEffect(() => { packagesRef.current = packages; }, [packages]);

  const pharmaciesRef    = useRef<Pharmacy[]>(pharmacies);
  useEffect(() => { pharmaciesRef.current = pharmacies; }, [pharmacies]);

  const handleNewScan = useCallback(async (address: Address, scannedPharmacyName?: string) => {
    const currentSession = getSession();
    const isKoerier  = currentSession?.user?.role === UserRole.COURIER;
    const courierId  = isKoerier ? currentSession?.user?.courierId : undefined;

    // Standaard fallback: session-pharmacy of currentPharmacy
    let pharmacyId: string =
      currentSession?.user?.pharmacyId ?? currentPharmacy.id;
    let pharmacyName: string =
      pharmaciesRef.current.find(p => p.id === pharmacyId)?.name ?? currentPharmacy.name;

    // Apotheek-herkenning op basis van labelnaam — zoek in ALLE pharmacies.
    const normalize = (s: string) =>
      s.toLowerCase().replace(/apotheek|pharmacy/gi, '').trim();
    const matchPharmacyByLabel = (label: string): Pharmacy | undefined => {
      const normalizedLabel = normalize(label);
      if (normalizedLabel.length === 0) return undefined;
      return pharmaciesRef.current.find(p => {
        const normalizedPharmacy = normalize(p.name);
        return normalizedPharmacy.length > 0 && (
          normalizedPharmacy.includes(normalizedLabel) ||
          normalizedLabel.includes(normalizedPharmacy)
        );
      });
    };

    if (isKoerier) {
      // KOERIER: de door de koerier gekozen actieve apotheek bepaalt de koppeling.
      // OCR-label is enkel een controle/waarschuwing en wordt nooit bepalend.
      const activeId = activeScanPharmacyIdRef.current ?? courierPharmacyIds[0];
      if (activeId) {
        pharmacyId = activeId;
        pharmacyName = pharmaciesRef.current.find(p => p.id === activeId)?.name ?? pharmacyName;
        // Zorg dat de actieve apotheek in de rit zit — vult de pills en de route-modal.
        setCourierPharmacyIds(prev => prev.includes(activeId) ? prev : [...prev, activeId]);
      }

      // OCR enkel als controle: een BEKENDE apotheek die afwijkt van de actieve → waarschuwen.
      if (scannedPharmacyName) {
        const match = matchPharmacyByLabel(scannedPharmacyName);
        if (match && match.id !== pharmacyId) {
          console.warn('[Scan] OCR-label wijkt af van actieve apotheek:', match.name, '≠', pharmacyName);
          setPharmacyMismatch(match.name);
        }
      }
    } else if (scannedPharmacyName) {
      // NIET-KOERIER (apotheek-account): bestaande OCR-gebaseerde herkenning — ongewijzigd.
      const match = matchPharmacyByLabel(scannedPharmacyName);
      if (match) {
        pharmacyId = match.id;
        pharmacyName = match.name;
        console.log('[Scan] Apotheek automatisch herkend:', match.name);
        // Voeg toe aan de rit-apotheken — vult de pills bovenaan en de route-modal.
        setCourierPharmacyIds(prev => {
          if (prev.includes(match.id)) return prev;
          console.log('[Scan] Apotheek toegevoegd aan rit:', match.name);
          return [...prev, match.id];
        });
      } else {
        // Onbekende apotheek — sla pakket op met label-naam maar zonder pharmacyId koppeling
        console.warn('[Scan] Apotheek niet herkend:', scannedPharmacyName, '— pakket krijgt label-naam, geen ID');
        pharmacyId = '';
        pharmacyName = scannedPharmacyName;
        setUnknownPharmacy(scannedPharmacyName);
      }
    }

    // Atomisch scanNumber — ref verhoogt direct zodat parallelle aanroepen altijd unieke nummers krijgen
    const scanNumber = nextScanNumberRef.current++;

    // Lees actuele packages via ref — niet via stale closure
    const currentPackages = packagesRef.current;
    // Een verse scan krijgt bewust GEEN routeIndex. Hij is niet geoptimaliseerd,
    // dus hij heeft geen plek in de route. Met een verzonnen positie (max + 1)
    // belandde hij onderaan de lijst, terwijl de koerier juist moet kunnen
    // controleren dat de scan gelukt is. CourierView zet pakketten zonder
    // routeIndex bovenaan, nieuwste eerst.
    const hasRoute = currentPackages.some(p => p.routeIndex !== undefined);

    const normalizedAddress: Address = {
      ...address,
      postalCode: normalizePostcode(address.postalCode ?? ''),
    };

    const pkg: Package = {
      id: `pkg-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
      pharmacyId,
      pharmacyName,
      address: normalizedAddress,
      status: isKoerier ? PackageStatus.PICKED_UP : PackageStatus.PENDING,
      courierId,
      courierName: courierId ? (COURIER_NAMES[courierId] ?? currentSession?.user?.name ?? courierId) : undefined,
      createdAt: new Date().toISOString(),
      priority: 3,
      scanNumber,
      statusHistory: [{
        status:    isKoerier ? PackageStatus.PICKED_UP : PackageStatus.PENDING,
        timestamp: new Date().toISOString(),
      }],
    };

    // TIJDELIJKE DIAGNOSTIEK [DubbelAdres] — verwijderen na de fix
    const key = addressKey(normalizedAddress);
    console.log('[DubbelAdres] scan binnen | key:', key,
      '| pkgId:', pkg.id, '| status:', pkg.status,
      '| courierId:', pkg.courierId, '| createdAt:', pkg.createdAt,
      '| adres:', normalizedAddress.street, normalizedAddress.houseNumber, normalizedAddress.postalCode);

    setPackages(prev => {
      const next = [pkg, ...prev];
      const sameKeyBefore = prev.filter(p => addressKey(p.address) === key).length;
      const sameKeyAfter  = next.filter(p => addressKey(p.address) === key).length;
      console.log('[DubbelAdres] setPackages | state', prev.length, '→', next.length,
        '| zelfde key vóór:', sameKeyBefore, '→ na:', sameKeyAfter,
        '| id al aanwezig:', prev.some(p => p.id === pkg.id));
      return next;
    });

    // Hoeveel nog niet afgeleverde pakketten liggen er nu op dit adres, inclusief
    // dit pakket? Puur informatief — er wordt niets geblokkeerd, twee patiënten
    // kunnen prima hetzelfde afleveradres hebben.
    // Alleen pakketten van vandaag — routes van gisteren mogen niet triggeren.
    const todayStr = new Date().toLocaleDateString('sv'); // YYYY-MM-DD lokale tijd
    // Alleen de eigen pakketten: een pakket van een collega op hetzelfde adres
    // gaf deze koerier anders een "tweede pakket"-waarschuwing die niet over hem
    // ging. Scant iemand zonder courierId (apotheek, beheer), dan is de
    // vergelijking undefined === undefined en telt diens eigen reeks mee.
    const sameAddressCount = currentPackages.filter(p =>
      OPEN_STATUSES.includes(p.status) &&
      addressKey(p.address) === key &&
      new Date(p.createdAt ?? 0).toLocaleDateString('sv') === todayStr &&
      p.courierId === courierId
    ).length + 1;

    // Let op: dit telt uit packagesRef (stand vóór deze scan) + 1, dus los van
    // de setPackages hierboven. Bij burst-scans kan die ref achterlopen.
    console.log('[DubbelAdres] sameAddressCount =', sameAddressCount,
      '| berekend uit packagesRef met', currentPackages.length, 'pakketten',
      '| open op deze key:', currentPackages.filter(p => OPEN_STATUSES.includes(p.status) && addressKey(p.address) === key).map(p => p.id));

    if (hasRoute) {
      setToast(`Pakket #${scanNumber} staat bovenaan je lijst — nog niet in de route. Optimaliseer opnieuw om het in te plannen.`);
      setTimeout(() => setToast(null), 4000);
    }

    const syncResult = await db.syncPackage(pkg);
    // TIJDELIJKE DIAGNOSTIEK [DubbelAdres]
    console.log('[DubbelAdres] syncPackage resultaat | id:', pkg.id,
      '| synced:', syncResult?.synced, '| error:', syncResult?.error ?? '—');
    if (syncResult && !syncResult.synced) {
      setToast(`Let op: pakket #${scanNumber} staat lokaal maar is NIET op de server opgeslagen. Controleer je verbinding en login, en scan zo nodig opnieuw.`);
      setTimeout(() => setToast(null), 8000);
    }

    // Geocodeer op de achtergrond — blokkeert de UI niet
    geocodeAddress(address).then(coords => {
      if (!coords) return;
      const updatedPkg = { ...pkg, address: { ...pkg.address, lat: coords.lat, lng: coords.lng } };
      setPackages(prev => prev.map(p => p.id === pkg.id ? updatedPkg : p));
      db.syncPackage(updatedPkg).catch(err => console.error('[Geocode] Sync naar DB mislukt:', err));
    }).catch(err => console.error('[Geocode] Onverwachte fout:', err));

    return { sameAddressCount };
  }, [currentPharmacy]); // packages/pharmacies + actieve apotheek via refs; courierPharmacyIds[0] is enkel een harmloze fallback

  const handleOptimizeRoute = useCallback(async (
    selectedIds: string[],
    startFrom: string = 'pharmacy',
    returnTo: string = 'pharmacy'
  ) => {
    if (selectedIds.length === 0) {
      setToast('Geen pakketten geselecteerd.');
      setTimeout(() => setToast(null), 3000);
      return;
    }
    setIsOptimizing(true);

    try {
      // Uit de ref en niet uit de closure: deze callback wordt alleen herbouwd
      // bij een andere apotheek, dus `packages` zou hier de stand van toen zijn.
      // Een koerier die scant en meteen de route start zag zijn verse pakketten
      // anders niet terug in selectedPackages — lege stoplijst, stille terugval.
      const selectedPackages = packagesRef.current.filter(p => selectedIds.includes(p.id));

      if (selectedPackages.length === 0) {
        console.warn('[Route] Geen pakketten gevonden voor IDs:', selectedIds);
        setToast('Pakketten niet gevonden — probeer opnieuw.');
        setTimeout(() => setToast(null), 3000);
        setIsOptimizing(false);
        return;
      }

      const stops = selectedPackages.map(p => ({
        id:          p.id,
        street:      p.address.street,
        houseNumber: p.address.houseNumber,
        postalCode:  p.address.postalCode,
        city:        p.address.city,
      }));

      // Bepaal startadres: 'current' = GPS, 'pharmacy' = legacy currentPharmacy,
      // elke andere string wordt als pharmacyId behandeld.
      let startAddress: string | null = null;
      if (startFrom === 'current') {
        startAddress = await new Promise<string | null>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            pos => resolve(`${pos.coords.latitude},${pos.coords.longitude}`),
            ()  => resolve(null),
            { enableHighAccuracy: true, timeout: 5000 }
          );
        });
      } else {
        const startPharmacy = startFrom === 'pharmacy'
          ? currentPharmacy
          : (pharmacies.find(p => p.id === startFrom) ?? currentPharmacy);
        if (startPharmacy?.address) {
          startAddress = `${startPharmacy.address}, Netherlands`;
        } else if (startPharmacy?.street && startPharmacy?.postalCode) {
          // Nieuwere apotheken hebben geen samengesteld `address` meer maar losse
          // velden. Zonder deze tak bleef startAddress null en vertrok de route
          // zonder startpunt.
          startAddress = `${startPharmacy.street} ${startPharmacy.houseNumber ?? ''}, ${startPharmacy.postalCode} ${startPharmacy.city ?? ''}, Netherlands`;
        }
      }

      // Bepaal eindadres: 'none' = geen terugreis, 'pharmacy' = legacy currentPharmacy,
      // elke andere string wordt als pharmacyId behandeld.
      let endAddress: string | null = null;
      if (returnTo !== 'none') {
        const endPharmacy = returnTo === 'pharmacy'
          ? currentPharmacy
          : (pharmacies.find(p => p.id === returnTo) ?? currentPharmacy);
        if (endPharmacy?.address) {
          endAddress = `${endPharmacy.address}, Netherlands`;
        } else if (endPharmacy?.street && endPharmacy?.postalCode) {
          endAddress = `${endPharmacy.street} ${endPharmacy.houseNumber ?? ''}, ${endPharmacy.postalCode} ${endPharmacy.city ?? ''}, Netherlands`;
        }
      }

      const { orderedIds, coords, totalDistanceM, totalDurationS } =
        await optimizeRouteDetailed(stops, startAddress, endAddress);
      setRouteGeometry({ orderedIds, coords, totalDistanceM, totalDurationS });

      console.log('=== ROUTE OPTIMALISATIE ===');
      console.log('Geselecteerde IDs:', selectedIds);
      console.log('Geoptimaliseerde volgorde:', orderedIds);
      orderedIds.forEach((id, i) => {
        const pkg = packagesRef.current.find(p => p.id === id);
        if (pkg) console.log(`Stop ${i + 1}: ${pkg.address.street} ${pkg.address.houseNumber}`);
      });

      // indexMap: id → 1-gebaseerde positie
      const indexMap = new Map() as Map<string, number>;
      orderedIds.forEach((id, i) => indexMap.set(id, i + 1));

      const metRoutepositie = (pkg: Package, pos: number): Package => ({
        ...pkg,
        status:       PackageStatus.ASSIGNED,
        routeIndex:   pos,
        displayIndex: pos,
        orderIndex:   pos - 1, // 0-gebaseerd voor overzicht-sort
      });

      // toSync wordt hier gebouwd en niet in de setPackages-updater: die draait
      // React pas bij de volgende render en mag hem meer dan één keer aanroepen.
      // Een push daarbinnen zou de regels hieronder een lege — of dubbele — lijst
      // geven, en dan gaat de route wel het scherm op maar nooit de server in.
      const toSync: Package[] = selectedPackages
        .filter(pkg => indexMap.has(pkg.id))
        .map(pkg => metRoutepositie(pkg, indexMap.get(pkg.id)!));

      console.log('Gesynchroniseerd:', toSync.map(p => `stop ${p.routeIndex}: ${p.address.street} ${p.address.houseNumber}`));

      // Functionele vorm: tussen het begin van deze callback en dit punt zitten
      // meerdere awaits (GPS, geocoding, de optimalisatie zelf). In die tijd kan
      // een scan of een realtime-update de lijst hebben gewijzigd; `packages` uit
      // de closure is dan verouderd en zou die wijzigingen overschrijven.
      setPackages(prev => prev.map(pkg =>
        indexMap.has(pkg.id) ? metRoutepositie(pkg, indexMap.get(pkg.id)!) : pkg
      ));
      const routeSync = await db.syncMultiplePackages(toSync);
      if (routeSync && !routeSync.synced) {
        setToast('Let op: de route is lokaal bijgewerkt maar NIET op de server opgeslagen. Controleer je verbinding en login.');
        setTimeout(() => setToast(null), 8000);
      }

    } catch (err) {
      console.error('Route optimalisatie mislukt:', err);
      alert('Routeoptimalisatie mislukt. Probeer opnieuw.');
    } finally {
      setIsOptimizing(false);
    }
  }, [pharmacies, currentPharmacy]); // packages via packagesRef, dus geen dependency

  const handleInstitutionRoute = useCallback(async (
    selected: Institution[],
    startFrom: string = 'pharmacy',
    returnTo: string = 'pharmacy'
  ) => {
    if (selected.length === 0) return;
    setIsOptimizing(true);
    setShowInstitutionSelector(false);

    try {
      // Converteer instellingen met een bruikbaar adres naar het optimizeRoute-formaat
      const addresses = selected
        .filter(i => i.street && i.postalCode)
        .map(i => ({
          id:          i.id,
          street:      i.street!,
          houseNumber: i.houseNumber ?? '',
          postalCode:  i.postalCode!,
          city:        i.city ?? '',
          lat:         i.addressLat,
          lng:         i.addressLng,
        }));

      // Adres van de actieve apotheek (koerier: actieve rit-apotheek)
      const activePharmacy = pharmacies.find(p => p.id === courierPharmacyIds[0]) ?? currentPharmacy;

      // Bepaal startadres — 'current' = GPS, 'pharmacy' = legacy fallback,
      // andere string = pharmacyId
      let startAddress: string | null = null;
      if (startFrom === 'current') {
        startAddress = await new Promise<string | null>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            pos => resolve(`${pos.coords.latitude},${pos.coords.longitude}`),
            ()  => resolve(null),
            { enableHighAccuracy: true, timeout: 5000 }
          );
        });
      } else {
        const startPharmacy = startFrom === 'pharmacy'
          ? activePharmacy
          : (pharmacies.find(p => p.id === startFrom) ?? activePharmacy);
        if (startPharmacy?.address) {
          startAddress = `${startPharmacy.address}, Netherlands`;
        } else if (startPharmacy?.street && startPharmacy?.postalCode) {
          // Zie handleOptimizeRoute: nieuwere apotheken hebben geen samengesteld
          // `address` meer maar losse velden.
          startAddress = `${startPharmacy.street} ${startPharmacy.houseNumber ?? ''}, ${startPharmacy.postalCode} ${startPharmacy.city ?? ''}, Netherlands`;
        }
      }

      // Bepaal eindadres — 'none' = geen terugreis, 'pharmacy' = legacy fallback,
      // andere string = pharmacyId
      let endAddress: string | null = null;
      if (returnTo !== 'none') {
        const endPharmacy = returnTo === 'pharmacy'
          ? activePharmacy
          : (pharmacies.find(p => p.id === returnTo) ?? activePharmacy);
        if (endPharmacy?.address) {
          endAddress = `${endPharmacy.address}, Netherlands`;
        } else if (endPharmacy?.street && endPharmacy?.postalCode) {
          endAddress = `${endPharmacy.street} ${endPharmacy.houseNumber ?? ''}, ${endPharmacy.postalCode} ${endPharmacy.city ?? ''}, Netherlands`;
        }
      }

      let orderedInstitutions: Institution[] = selected;
      if (addresses.length > 1) {
        const orderedIds = await optimizeRoute(addresses, startAddress, endAddress);
        const ordered = orderedIds
          .map(id => selected.find(i => i.id === id))
          .filter(Boolean) as Institution[];
        // instellingen zonder adres (niet geoptimaliseerd) achteraan toevoegen
        const rest = selected.filter(i => !ordered.some(o => o.id === i.id));
        orderedInstitutions = [...ordered, ...rest];
      }

      setActiveInstitutionRoute(orderedInstitutions);
    } catch (err) {
      console.error('Instelling route mislukt:', err);
      alert('Routeplanning mislukt. Probeer opnieuw.');
    } finally {
      setIsOptimizing(false);
    }
  }, [pharmacies, courierPharmacyIds, currentPharmacy]);

  const BESCHERMDE_STATUSSEN = [
    PackageStatus.DELIVERED,
    PackageStatus.MAILBOX,
    PackageStatus.NEIGHBOUR,
    PackageStatus.RETURN,
    PackageStatus.MOVED,
    PackageStatus.OTHER_LOCATION,
    PackageStatus.NOT_HOME,
    PackageStatus.FAILED,
  ];

  const updateMultipleStatus = async (ids: string[], status: PackageStatus, evidence?: DeliveryEvidence) => {
    // Beschermde statussen mogen nooit overschreven worden door REMOVED
    const safeIds = status === PackageStatus.REMOVED
      ? ids.filter(id => {
          const pkg = packages.find(p => p.id === id);
          return pkg && !BESCHERMDE_STATUSSEN.includes(pkg.status);
        })
      : ids;

    if (safeIds.length < ids.length) {
      console.warn(
        `[Status] ${ids.length - safeIds.length} pakket(jes) overgeslagen — beschermde status kan niet overschreven worden door REMOVED`
      );
    }
    if (safeIds.length === 0) return;

    const pkgsToSync: Package[] = [];
    const newPackages = packages.map(p => {
      if (safeIds.includes(p.id)) {
        const newEvent: StatusEvent = {
          status,
          timestamp: evidence?.timestamp ?? new Date().toISOString(),
          note:      evidence?.deliveryNote,
        };
        // Terug in de actieve groep, of uit de rit gehaald? Dan hoort er geen
        // bezorgtijdstip meer aan te hangen. Bleef dat staan, dan hield een pakket
        // dat nog bezorgd moest worden een deliveredAt uit een eerdere poging —
        // wat doorwerkt in de urenberekening en in de Track & Trace-tekst.
        // Expliciet null, niet undefined: undefined valt weg bij JSON.stringify
        // en laat de kolom in Supabase dan ongemoeid.
        const backToActive = isActionableStatus(status) || status === PackageStatus.REMOVED;
        const updated: Package = {
          ...p,
          status,
          deliveryEvidence: evidence,
          deliveredAt: backToActive ? null : (evidence?.timestamp ?? p.deliveredAt),
          statusHistory: [...(p.statusHistory ?? [{ status: p.status, timestamp: p.createdAt }]), newEvent],
        };
        pkgsToSync.push(updated);
        return updated;
      }
      return p;
    });
    setPackages(newPackages);
    const statusSync = await db.syncMultiplePackages(pkgsToSync);
    if (statusSync && !statusSync.synced) {
      setToast('Let op: statuswijziging staat lokaal maar is NIET op de server opgeslagen. Controleer je verbinding en login.');
      setTimeout(() => setToast(null), 8000);
    }
  };

  const isActionable = (pkg: Package): boolean => isActionableStatus(pkg.status);

  const handleNewRit = useCallback(() => {
    if (!confirm('Nieuwe rit starten? De huidige rit wordt gearchiveerd.')) return;
    // Verwijder alle pakketten van deze koerier uit lokale state
    setPackages(prev => prev.filter(p => p.courierId !== session?.user.courierId));
    // Reset apotheek-lijst — wordt automatisch opnieuw gevuld zodra de koerier scant
    setCourierPharmacyIds([]);
    localStorage.removeItem('courierPharmacyIds');
    setActiveInstitutionRoute([]);
  }, [session]);

  const handleAddPharmacy = async (newPharmacy: Pharmacy) => {
    // 1. Direct toevoegen aan lokale state (optimistic)
    setPharmacies(prev => [...prev, newPharmacy]);

    // 2. Opslaan via db (localStorage + Supabase)
    try {
      await db.savePharmacy(newPharmacy);
    } catch (err: any) {
      console.error('Opslaan apotheek mislukt:', err);
      alert('Apotheek opslaan in de cloud mislukt:\n\n' + (err?.message || err) + '\n\nDe apotheek is NIET opgeslagen.');
    }

    // 3. Herlaad vanuit Supabase zodat de lijst altijd de server-state weerspiegelt
    if (supabase) {
      const { data } = await supabase.from('pharmacies').select('*').order('name');
      if (data && data.length > 0) setPharmacies(data);
    }
  };

  const [editingPharmacy, setEditingPharmacy] = useState<Pharmacy | null>(null);

  const handleUpdatePharmacy = async (updated: Pharmacy) => {
    // Normaliseer code ↔ courierCode (UI gebruikt 'code', DB-kolom is 'courierCode')
    const normalized: Pharmacy = {
      ...updated,
      code:        updated.code ?? updated.courierCode,
      courierCode: updated.courierCode ?? updated.code,
    };
    setPharmacies(prev => prev.map(p => p.id === normalized.id ? normalized : p));
    try {
      await db.savePharmacy(normalized);
    } catch (err) {
      console.error('Opslaan apotheek mislukt:', err);
      alert('Opslaan mislukt. Probeer opnieuw.');
      return;
    }
    if (supabase) {
      const { data } = await supabase.from('pharmacies').select('*').order('name');
      if (data && data.length > 0) setPharmacies(data);
    }
  };

  const handleDeletePharmacy = async (pharmacy: Pharmacy) => {
    const packageCount = packages.filter(p => p.pharmacyId === pharmacy.id).length;

    if (packageCount > 0) {
      alert(
        `"${pharmacy.name}" kan niet verwijderd worden: er ${packageCount === 1 ? 'hangt nog 1 pakket' : `hangen nog ${packageCount} pakketten`} aan deze apotheek.\n\nVerplaats die eerst naar een andere apotheek (via "Niet-toegewezen pakketten" of door ze te herkoppelen). Daarna kun je de apotheek verwijderen.`
      );
      return;
    }

    if (!confirm(`Weet je zeker dat je "${pharmacy.name}" wilt verwijderen?`)) return;

    try {
      await db.deletePharmacy(pharmacy.id);
      setPharmacies(prev => prev.filter(p => p.id !== pharmacy.id));
      setEditingPharmacy(null);
    } catch (err) {
      console.error('Verwijderen mislukt:', err);
      alert('Verwijderen mislukt. Probeer opnieuw.');
    }
  };

  const handlePharmacyCodeChange = useCallback((pharmacyId: string, code: string) => {
    setPharmacies(prev => prev.map(p => p.id === pharmacyId ? { ...p, courierCode: code } : p));
  }, []);

  const canAddPharmacy = role === UserRole.SUPERUSER || role === UserRole.SUPERVISOR;

  const copySQL = () => {
    const sql = `CREATE TABLE IF NOT EXISTS packages (
  id TEXT PRIMARY KEY,
  "pharmacyId" TEXT,
  "pharmacyName" TEXT,
  address JSONB,
  status TEXT,
  "courierId" TEXT,
  "courierName" TEXT,
  "createdAt" TIMESTAMPTZ DEFAULT NOW(),
  "deliveredAt" TIMESTAMPTZ,
  "deliveryEvidence" JSONB,
  priority INTEGER,
  "orderIndex" INTEGER,
  "displayIndex" INTEGER,
  "scanNumber" INTEGER,
  "routeIndex" INTEGER
);
ALTER TABLE packages ADD COLUMN IF NOT EXISTS "scanNumber" INTEGER;
ALTER TABLE packages ADD COLUMN IF NOT EXISTS "routeIndex" INTEGER;
ALTER TABLE packages ENABLE ROW LEVEL SECURITY;
DROP POLICY IF EXISTS "Allow public access" ON packages;
CREATE POLICY "Allow public access" ON packages FOR ALL USING (true);

CREATE TABLE IF NOT EXISTS chat_conversations (
  id TEXT PRIMARY KEY,
  "createdAt" TIMESTAMPTZ DEFAULT NOW(),
  "expiresAt" TIMESTAMPTZ,
  "pharmacyId" TEXT,
  messages JSONB DEFAULT '[]',
  "hasRiskSignal" BOOLEAN DEFAULT false,
  "callbackRequest" JSONB,
  "isRead" BOOLEAN DEFAULT false
);
ALTER TABLE chat_conversations ENABLE ROW LEVEL SECURITY;
DROP POLICY IF EXISTS "Allow public access" ON chat_conversations;
CREATE POLICY "Allow public access" ON chat_conversations FOR ALL USING (true);
ALTER publication supabase_realtime ADD TABLE chat_conversations;

CREATE TABLE IF NOT EXISTS pharmacies (
  id TEXT PRIMARY KEY,
  name TEXT NOT NULL,
  address TEXT,
  "groupId" TEXT,
  "courierCode" TEXT
);
ALTER TABLE pharmacies ADD COLUMN IF NOT EXISTS "courierCode" TEXT;
ALTER TABLE pharmacies ENABLE ROW LEVEL SECURITY;
DROP POLICY IF EXISTS "Allow public access" ON pharmacies;
CREATE POLICY "Allow public access" ON pharmacies FOR ALL USING (true);

CREATE TABLE IF NOT EXISTS institutions (
  id TEXT PRIMARY KEY,
  "pharmacyId" TEXT NOT NULL REFERENCES pharmacies(id),
  name TEXT NOT NULL,
  address TEXT NOT NULL,
  street TEXT,
  "houseNumber" TEXT,
  "postalCode" TEXT,
  city TEXT,
  "addressLat" DOUBLE PRECISION,
  "addressLng" DOUBLE PRECISION,
  frequency TEXT DEFAULT 'weekly',
  "deliveryDays" TEXT[] DEFAULT '{}',
  instructions TEXT,
  "contactPerson" TEXT,
  "contactPhone" TEXT,
  "isActive" BOOLEAN DEFAULT true,
  "createdAt" TIMESTAMPTZ DEFAULT NOW()
);
ALTER TABLE institutions ENABLE ROW LEVEL SECURITY;
DROP POLICY IF EXISTS "Allow public access" ON institutions;
CREATE POLICY "Allow public access" ON institutions FOR ALL USING (true);`;
    navigator.clipboard.writeText(sql);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Render: sessie wordt nog gecontroleerd ───────────────────────
  if (sessionChecking && !session && !showPatientView) {
    return (
      <div className="h-dvh flex items-center justify-center bg-[#f7f9fb]">
        <Loader2 size={28} className="animate-spin text-[#006b5a]" />
      </div>
    );
  }

  // ── Render: not logged in ────────────────────────────────────────
  if (!session && !showPatientView) {
    return (
      <LoginScreen
        onLogin={handleLogin}
        onGuestAccess={() => setShowPatientView(true)}
      />
    );
  }

  // ── Render: patient (no login) ───────────────────────────────────
  if (showPatientView) {
    return (
      <PatientView
        packages={packages}
        onBack={() => setShowPatientView(false)}
      />
    );
  }

  // ── Render: koerier zonder telefoonnummer ────────────────────────
  // Zolang de controle loopt (null) gaat de app gewoon open: wie zijn nummer al
  // heeft, hoort hier niets van te merken.
  if (session?.user.role === UserRole.COURIER && courierPhoneOk === false) {
    return (
      <PhoneNumberGate
        courierName={session.user.name}
        onSaved={() => setCourierPhoneOk(true)}
      />
    );
  }

  // ── Helpers for authenticated views ─────────────────────────────
  const syncIndicator = (
    <div className={`flex items-center space-x-2 px-3 py-1 border rounded-full transition-colors ${hasCloudConfig ? 'bg-slate-50 border-slate-200' : 'bg-amber-50 border-amber-100'}`}>
      {isSyncing ? (
        <RefreshCw size={14} className="text-[#006b5a] animate-spin" />
      ) : hasCloudConfig ? (
        <Cloud size={14} className="text-emerald-500" />
      ) : (
        <CloudOff size={14} className="text-amber-500" />
      )}
      <span className={`text-[10px] font-black uppercase tracking-tighter ${hasCloudConfig ? 'text-slate-500' : 'text-amber-600'}`}>
        {isSyncing ? 'Synchroniseren...' : hasCloudConfig ? 'Cloud Actief' : 'Lokaal'}
      </span>
    </div>
  );

  const extraHeader = (
    <div className="flex items-center space-x-3">
      {syncIndicator}
    </div>
  );

  const couriers = [
    { id: 'k1', name: 'Marco Koerier', role: UserRole.COURIER, status: CourierStatus.AVAILABLE },
    { id: 'k2', name: 'Sanne Koerier', role: UserRole.COURIER, status: CourierStatus.ON_ROUTE },
  ];

  const setupBanner = !hasCloudConfig && (role === UserRole.SUPERUSER || role === UserRole.ADMIN) && (
    <div className="mb-6 bg-amber-50 border border-amber-200 p-6 rounded-4xl shadow-sm">
      <div className="flex items-start space-x-4">
        <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center text-amber-600 shrink-0">
          <AlertTriangle size={24} />
        </div>
        <div className="flex-1">
          <p className="text-lg font-black text-amber-900">Database niet geconfigureerd</p>
          <p className="text-xs font-bold text-amber-700 uppercase tracking-widest mt-1 opacity-80">
            Data wordt uitsluitend op dit apparaat bewaard.
          </p>
          <button
            onClick={() => setShowSetupHelp(!showSetupHelp)}
            className="mt-4 flex items-center space-x-2 text-[10px] font-black uppercase tracking-widest text-white bg-amber-600 px-4 py-2 rounded-xl hover:bg-amber-700 transition-all shadow-md shadow-amber-200"
          >
            <span>{showSetupHelp ? 'Verberg' : 'Nu configureren'}</span>
            {showSetupHelp ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {showSetupHelp && (
            <div className="mt-6 p-5 bg-white border border-amber-200 rounded-3xl animate-in slide-in-from-top-4 duration-500 shadow-xl shadow-amber-900/5">
              <div className="flex items-center space-x-2 mb-4 text-[#006b5a]">
                <Info size={16} />
                <p className="text-xs font-black uppercase tracking-tighter">Stap-voor-stap Setup</p>
              </div>
              <p className="text-xs font-black text-slate-800 mb-3">1. Voer dit uit in Supabase SQL Editor:</p>
              <div className="relative group">
                <pre className="text-[10px] font-mono bg-slate-900 text-slate-300 p-4 rounded-2xl overflow-x-auto leading-relaxed border border-slate-800">
                  {`CREATE TABLE packages (...); -- Klik copy voor SQL`}
                </pre>
                <button
                  onClick={copySQL}
                  className="absolute top-2 right-2 p-2 text-white rounded-xl transition-all flex items-center space-x-2 shadow-lg active:scale-95"
                  style={{ background: 'linear-gradient(135deg, #006b5a, #48c2a9)' }}
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  <span className="text-[8px] font-black uppercase">{copied ? 'Gekopieerd' : 'Copy SQL'}</span>
                </button>
              </div>
              <div className="mt-6 space-y-2">
                <p className="text-xs font-black text-slate-800">2. Voeg toe in Netlify → Environment Variables:</p>
                {['VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY', 'GEMINI_API_KEY'].map(k => (
                  <div key={k} className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                    <code className="text-[10px] font-black text-[#006b5a]">{k}</code>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ── Render: authenticated views ──────────────────────────────────
  return (
    <Layout
      userName={session!.user.name}
      userRole={role || ''}
      onLogout={handleLogout}
      hideMobileNav={showScanner}
      extraHeaderContent={extraHeader}
      {...(role === UserRole.COURIER && dienstCheckOk ? {
        mobileNav: {
          items: [
            { id: 'bezorgen', icon: Truck,    label: 'Mijn Rit' },
            { id: 'rooster',  icon: Calendar, label: 'Rooster'  },
          ],
          activeId: courierTab,
          onSelect: (id: string) => setCourierTab(id as 'bezorgen' | 'rooster'),
        },
      } : {})}
    >
      {/* Toast notificatie */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl text-sm font-black animate-in slide-in-from-top duration-300 whitespace-nowrap">
          💬 {toast}
        </div>
      )}

      {/* Verlopen sessie: opnieuw proberen helpt niet, alleen opnieuw inloggen. */}
      {sessionExpired && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] max-w-[92vw] bg-red-600 text-white px-5 py-3 rounded-2xl shadow-2xl text-sm font-bold animate-in slide-in-from-top duration-300 flex items-center gap-3">
          <span>{packagesError ?? 'Je sessie is verlopen.'}</span>
          <button
            onClick={async () => {
              await logout();
              setSession(null);
              setPackages([]);
              setPackagesError(null);
              setSessionExpired(false);
              setCloudStale(false);
            }}
            className="underline font-black whitespace-nowrap"
          >
            Opnieuw inloggen
          </button>
        </div>
      )}

      {/* Golf 2b: melding bij mislukte cloud-lees (mogelijk verouderde data) */}
      {cloudStale && !sessionExpired && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] max-w-[92vw] bg-amber-500 text-white px-5 py-3 rounded-2xl shadow-2xl text-sm font-bold animate-in slide-in-from-top duration-300 flex items-center gap-3">
          <span>
            {packagesError
              ? `Pakketten konden niet geladen worden: ${packagesError}`
              : 'Verbinding met de server mislukt — je ziet mogelijk verouderde gegevens.'}
          </span>
          <button onClick={() => location.reload()} className="underline font-black whitespace-nowrap">Opnieuw proberen</button>
        </div>
      )}

      <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
        {setupBanner}

        {/* Apotheek-mismatch waarschuwing */}
        {pharmacyMismatch && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-3xl p-4 flex items-start space-x-3 animate-in slide-in-from-top-2 duration-300">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-black text-amber-900">Label van een andere apotheek</p>
              <p className="text-xs font-bold text-amber-700 mt-1 leading-relaxed">
                Op het label staat: <span className="font-black">{pharmacyMismatch}</span>.
                Actieve apotheek: <span className="font-black">{currentPharmacy.name}</span>.
                Controleer of dit pakket bij de juiste apotheek hoort.
              </p>
            </div>
            <button
              onClick={() => setPharmacyMismatch(null)}
              className="text-amber-400 hover:text-amber-700 transition-colors shrink-0"
              aria-label="Sluit melding"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Onbekende apotheek — label hoort bij geen bekende apotheek */}
        {unknownPharmacy && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-3xl p-4 flex items-start space-x-3 animate-in slide-in-from-top-2 duration-300">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-black text-amber-900">Onbekende apotheek</p>
              <p className="text-xs font-bold text-amber-700 mt-1 leading-relaxed">
                Het label <span className="font-black">{unknownPharmacy}</span> hoort bij geen bekende apotheek.
                Het pakket is wél vastgelegd en gemarkeerd, zodat een beheerder het later aan de juiste apotheek kan koppelen.
              </p>
            </div>
            <button
              onClick={() => setUnknownPharmacy(null)}
              className="text-amber-400 hover:text-amber-700 transition-colors shrink-0"
              aria-label="Sluit melding"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* PLANNER-TOEGANG naast een andere rol — de rol zelf houdt zijn eigen
            scherm; deze balk is de enige toevoeging. Bij role === PLANNER staat
            er hieronder al een hele doorverwijspagina, dus dan geen balk. */}
        {hasPlannerAccess && role !== UserRole.PLANNER && (
          <div className="mb-4 bg-[#006b5a]/5 border border-[#006b5a]/20 rounded-3xl p-4 flex items-center gap-3">
            <CalendarDays size={18} className="text-[#006b5a] shrink-0" />
            <p className="flex-1 text-sm font-black text-[#006b5a] min-w-0">
              Je hebt toegang tot de GoBob Planner
            </p>
            <a
              href="https://planner.go-bob.nl"
              target="_blank"
              rel="noopener noreferrer"
              className="h-9 px-3 rounded-xl bg-[#006b5a] hover:bg-[#00594b] transition-colors text-white text-sm font-bold flex items-center gap-1.5 shrink-0"
            >
              Openen
              <ArrowUpRight size={15} />
            </a>
          </div>
        )}

        {/* SUPERUSER — systeem-breed overzicht */}
        {role === UserRole.SUPERUSER && (
          <SuperuserView
            packages={accessiblePackages}
            pharmacies={accessiblePharmacies}
            userRole={UserRole.SUPERUSER}
            packagesError={packagesError}
            packageCounts={packageCounts}
            onUpdateStatus={updateMultipleStatus}
            canAddPharmacy={canAddPharmacy}
            onAddPharmacy={handleAddPharmacy}
            onEditPharmacy={setEditingPharmacy}
            onOptimize={handleOptimizeRoute}
            isOptimizing={isOptimizing}
            onPharmacyCodeChange={handlePharmacyCodeChange}
          />
        )}

        {/* ADMIN — één of meerdere apotheken beheren */}
        {role === UserRole.ADMIN && (
          <AdminView
            packages={accessiblePackages}
            pharmacies={accessiblePharmacies}
            conversations={conversations}
            packagesError={packagesError}
            packageCounts={packageCounts}
            onMarkConversationRead={handleMarkConversationRead}
            onMarkCallbackHandled={handleMarkCallbackHandled}
            onOptimize={handleOptimizeRoute}
            isOptimizing={isOptimizing}
            onPharmacyCodeChange={handlePharmacyCodeChange}
          />
        )}

        {/* PHARMACY — overzicht + chats */}
        {role === UserRole.PHARMACY && (
          <PharmacyView
            packages={visiblePackages}
            pharmacyName={currentPharmacy.name}
            pharmacyId={currentPharmacy.id}
            pharmacyCourierCode={currentPharmacy.courierCode}
            conversations={conversations}
            onMarkConversationRead={handleMarkConversationRead}
            onMarkCallbackHandled={handleMarkCallbackHandled}
          />
        )}

        {/* COURIER — eerst de dienst-check (locatie/camera), dan de rit */}
        {role === UserRole.COURIER && !dienstCheckOk && (
          <DienstCheck
            courierName={session?.user.name}
            onReady={() => {
              try { sessionStorage.setItem('gs_dienstcheck_ok', '1'); } catch {}
              setDienstCheckOk(true);
            }}
          />
        )}

        {/* COURIER — schakelen tussen de rit en het eigen rooster (desktop;
            op mobiel gaat dit via de onderbalk) */}
        {role === UserRole.COURIER && dienstCheckOk && (
          <div className="hidden lg:flex border-b border-slate-200 bg-white">
            <button
              onClick={() => setCourierTab('bezorgen')}
              className={`flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                courierTab === 'bezorgen'
                  ? 'border-[#006b5a] text-[#006b5a]'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Bezorgen
            </button>
            <button
              onClick={() => setCourierTab('rooster')}
              className={`flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                courierTab === 'rooster'
                  ? 'border-[#006b5a] text-[#006b5a]'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Mijn rooster
            </button>
          </div>
        )}

        {/* COURIER — kies/koppel eerst de apotheek waarvoor je vandaag scant */}
        {role === UserRole.COURIER && dienstCheckOk && courierTab === 'bezorgen' && (activeScanPharmacyId === null || showPharmacyPicker) && (
          <CourierPharmacyLink
            pharmacies={pharmacies}
            linkedIds={courierPharmacyIds}
            onLinked={(id) => setCourierPharmacyIds(prev => prev.includes(id) ? prev : [...prev, id])}
            onChoose={(id) => {
              setActiveScanPharmacyId(id);
              setCourierPharmacyIds(prev => prev.includes(id) ? prev : [...prev, id]);
              setShowPharmacyPicker(false);
            }}
            {...(activeScanPharmacyId !== null ? { onSkip: () => setShowPharmacyPicker(false) } : {})}
          />
        )}

        {/* COURIER — eigen rit, scannen en route plannen */}
        {role === UserRole.COURIER && dienstCheckOk && courierTab === 'bezorgen' && activeScanPharmacyId !== null && !showPharmacyPicker && (
          <CourierView
            packages={visiblePackages}
            onUpdate={() => {}}
            onUpdateMany={updateMultipleStatus}
            pharmacyName={courierPharmacyIds.length === 1
              ? (pharmacies.find(p => p.id === courierPharmacyIds[0])?.name ?? currentPharmacy.name)
              : courierPharmacyIds.length > 1
                ? `${courierPharmacyIds.length} apotheken`
                : currentPharmacy.name}
            pharmacyAddress={courierPharmacyIds.length === 1
              ? pharmacies.find(p => p.id === courierPharmacyIds[0])?.address
              : undefined}
            activePharmacyNames={courierPharmacyIds
              .map(id => pharmacies.find(p => p.id === id)?.name)
              .filter(Boolean) as string[]}
            activePharmacies={courierPharmacyIds
              .map(id => pharmacies.find(p => p.id === id))
              .filter(Boolean) as Pharmacy[]}
            activeScanPharmacyName={pharmacies.find(p => p.id === activeScanPharmacyId)?.name}
            onSwitchPharmacy={() => setShowPharmacyPicker(true)}
            onScanStart={() => setShowScanner(true)}
            onManualAdd={() => setShowManualForm(true)}
            onOptimize={handleOptimizeRoute}
            isOptimizing={isOptimizing}
            onNewRit={handleNewRit}
            onInstitutionRoute={() => setShowInstitutionSelector(true)}
            activeInstitutionRoute={activeInstitutionRoute}
            onOptimizeInstitutions={handleInstitutionRoute}
            routeGeometry={routeGeometry}
          />
        )}

        {/* COURIER — eigen rooster, alleen lezen */}
        {role === UserRole.COURIER && dienstCheckOk && courierTab === 'rooster' && (
          <CourierRooster />
        )}

        {/* REGIOMANAGER — alleen de eigen toegewezen apotheken, read-only */}
        {role === UserRole.REGION_MANAGER && <RegionManagerView />}

        {/* PLANNER — hoort in de planner-app; hier alleen een doorverwijzing */}
        {role === UserRole.PLANNER && <PlannerRedirectView />}

        {/* SUPERVISOR — zelfde overzicht als superuser, gefilterd op eigen apotheken */}
        {role === UserRole.SUPERVISOR && (
          <SuperuserView
            packages={accessiblePackages}
            pharmacies={accessiblePharmacies}
            userRole={UserRole.SUPERVISOR}
            packagesError={packagesError}
            packageCounts={packageCounts}
            onUpdateStatus={updateMultipleStatus}
            canAddPharmacy={canAddPharmacy}
            onAddPharmacy={handleAddPharmacy}
            onEditPharmacy={setEditingPharmacy}
            onOptimize={handleOptimizeRoute}
            isOptimizing={isOptimizing}
            onPharmacyCodeChange={handlePharmacyCodeChange}
          />
        )}

        {/* PATIENT — zou normaal niet hier komen (gaat via guest) */}
        {role === UserRole.PATIENT && (
          <PatientView packages={packages} />
        )}
      </div>

      {showScanner && (
        <Scanner
          onScanComplete={({ address, pharmacyName }) => handleNewScan(address, pharmacyName)}
          onSessionExpired={async () => {
            setShowScanner(false);
            await logout();
            setSession(null);
          }}
          onCancel={() => setShowScanner(false)}
        />
      )}

      {/* Vaste instellingen selecteren (koerier) */}
      {showInstitutionSelector && role === UserRole.COURIER && (
        <InstitutionSelector
          institutions={institutions}
          onStartRoute={handleInstitutionRoute}
          isOptimizing={isOptimizing}
          onClose={() => setShowInstitutionSelector(false)}
        />
      )}

      {showManualForm && (
        <ManualAddressForm
          onComplete={result => { handleNewScan(result.address, result.pharmacyName); setShowManualForm(false); }}
          onCancel={() => setShowManualForm(false)}
        />
      )}

      {/* Apotheek bewerken modal */}
      {editingPharmacy && (
        <EditPharmacyModal
          pharmacy={editingPharmacy}
          onSave={async (updated) => { await handleUpdatePharmacy(updated); setEditingPharmacy(null); }}
          onClose={() => setEditingPharmacy(null)}
          onDelete={role === UserRole.SUPERUSER ? handleDeletePharmacy : undefined}
        />
      )}

    </Layout>
  );
};

export default App;
